import { generateClient } from "aws-amplify/api";
import type { Subscription } from "rxjs";

import * as queries from "~/common/gqls/appsync/generated-query/queries";
import * as subscriptions from "~/common/gqls/appsync/generated-query/subscriptions";

type RunLogOutcome = "succeeded" | "failed" | "timeout";

const EXISTING_LOG_POLL_INTERVAL_MS = 2_000;

// A message is the pipeline's terminal marker for THIS run iff it mentions this jobRunId AND a [SUCCEEDED]/[FAILED] token.
// Note: the backend emits `jobRunID=` (capital I, capital D).
function outcomeFromMessage(text: string, jobRunId: string): "succeeded" | "failed" | null {
  if (!text.includes(`jobRunID=${jobRunId}`)) return null;
  if (text.includes("[SUCCEEDED]")) return "succeeded";
  if (text.includes("[FAILED]")) return "failed";
  return null;
}

export function awaitRunOutcomeFromLogs(
  jobRunId: string,
  opts?: { timeoutMs?: number; signal?: AbortSignal },
): Promise<RunLogOutcome> {
  return new Promise<RunLogOutcome>((resolve) => {
    // Guard against double-resolve
    let settled = false;

    let subscription: Subscription | undefined;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    let pollHandle: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      subscription?.unsubscribe();
      if (timeoutHandle !== undefined) {
        clearTimeout(timeoutHandle);
        timeoutHandle = undefined;
      }
      if (pollHandle !== undefined) {
        clearTimeout(pollHandle);
        pollHandle = undefined;
      }
      if (opts?.signal) {
        opts.signal.removeEventListener("abort", onAbort);
      }
    };

    const settle = (outcome: RunLogOutcome) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(outcome);
    };

    // If already aborted, resolve immediately
    if (opts?.signal?.aborted) {
      resolve("timeout");
      return;
    }

    // Step 1: Subscribe FIRST to catch markers that arrive during fetch
    const client = generateClient();
    subscription = client
      .graphql({
        query: subscriptions.tailLogMessages,
        variables: { jobRunId },
      })
      .subscribe({
        next: (event) => {
          const msg = event.data.tailLogMessages;
          if (msg) {
            const outcome = outcomeFromMessage(msg.message, jobRunId);
            if (outcome !== null) {
              settle(outcome);
            }
          }
        },
        error: () => {
          // Swallow subscription errors — let timeout fire
        },
      });

    // Step 2: Fetch existing messages (paginated) to handle already-completed runs
    const fetchPages = (nextToken?: string): Promise<void> => {
      const req = client.graphql({
        query: queries.queryMessages,
        variables: nextToken ? { jobRunId, nextToken } : { jobRunId },
      });
      return req.then((result) => {
        if (settled) return;
        const page = result.data.queryMessages;
        if (page) {
          for (const item of page.items) {
            const outcome = outcomeFromMessage(item.message, jobRunId);
            if (outcome !== null) {
              settle(outcome);
              return;
            }
          }
          if (page.nextToken) {
            return fetchPages(page.nextToken);
          }
        }
      });
    };

    const pollExistingMessages = () => {
      fetchPages()
        .catch(() => {
          // A later poll or subscription can still observe the terminal marker.
        })
        .finally(() => {
          if (!settled) {
            pollHandle = setTimeout(pollExistingMessages, EXISTING_LOG_POLL_INTERVAL_MS);
          }
        });
    };
    pollExistingMessages();

    // Step 3: Timeout
    timeoutHandle = setTimeout(
      () => {
        settle("timeout");
      },
      opts?.timeoutMs ?? 10 * 60 * 1000,
    );

    // Step 4: AbortSignal support
    const onAbort = () => settle("timeout");
    if (opts?.signal) {
      opts.signal.addEventListener("abort", onAbort);
    }
  });
}
